import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Typography,
  IconButton,
  Divider,
  TablePagination,
  TableSortLabel,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import LastPageIcon from "@mui/icons-material/LastPage";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import {
  DeleteIconSvg,
  EditIconSvg,
  ExpandlistMore,
  Optionicon,
} from "../../../../assets/svgIcons/Svg";
import CLSettings from "../CLSettings";
import ImportComments from "../ImportComments";
import { apiCallGet } from "../../../utils/action";
import {
  API_CONSTANTS,
  getApiCall,
  postApiCall,
} from "../../../config/api-constants";
import moment from "moment";
import Preloader from "../../../helpers/Preloader";
import CommentTags from "../CommentTags";
import FilterTags from "../FilterTags";
import EditOptionsDialog from "../../../common/froalaHtmlEditor/EditOptionsDialog";
import { useDispatch, useSelector } from "react-redux";
import {
  addGlobalList,
  deleteGlobalList,
  editGlobalList,
  getGlobalList,
  globalListTag,
} from "../../../store/action";
import CopySummaryModal from "../../../common/TemplateUtils/CopySummaryModal";

const TablePaginationActions = (props) => {
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    const lastPage = Math.max(0, Math.ceil(count / rowsPerPage) - 1);
    onPageChange(event, lastPage);
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        <FirstPageIcon />
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        <KeyboardArrowLeft />
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        <KeyboardArrowRight />
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        <LastPageIcon />
      </IconButton>
    </Box>
  );
};

const GlobalList = ({ importing, setImporting, setProgress }) => {
  const [order, setOrder] = useState("");
  const [orderBy, setOrderBy] = useState("");
  const dispatch = useDispatch();
  const globalList = useSelector((state) => state.globalList);
  const [tags, setTags] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(parseInt(sessionStorage.getItem("listsRowPerPage"), 10) || 10);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [openOption, setOpenOption] = useState(false);
  const [openCommentImport, setOpenCommentImport] = useState(false);
  const [importList, setImportList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteRowID, setDeleteRowID] = useState(null);
  const [refreshTs, setRefreshTs] = useState(Date.now());
  const [searchDebounce, setSearchDebounce] = useState("");
  const [editDialogState, setEditDialogState] = useState({
    open: false,
    newOption: "",
    dialogOptionList: [],
    globalLists: [],
    saveToGlobalLists: true,
    radioValue: "updateExisting",
    name: "",
    tags: [],
    description: "",
    listId: null,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const openOptionModal = () => setOpenOption(true);
  const closeOptionModal = () => setOpenOption(false);

  const GetCLSummaryList = (onSuccess) => {
    setIsLoading(true);
    apiCallGet(
      (response) => {
        const { data, message, success } = response?.data;
        if (success) {
          setImportList(data);
          if (onSuccess) onSuccess();
        } else {
          console.log("else", message);
        }
        setIsLoading(false);
      },
      (error) => {
        console.error("Error loading templates:", error);
        setIsLoading(false);
      },
      "GLOBAL_LIST_TEMPLATES_ACTIVE"
    );
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    const newOrder = isAsc ? "desc" : "asc";
    setOrder(newOrder);
    setOrderBy(property);
    setPage(0);
    setRefreshTs(Date.now());
  };

  useEffect(() => {
    async function GetGlobalLists() {
      setIsLoading(true);
      await dispatch(
        getGlobalList(
          page,
          rowsPerPage,
          searchDebounce,
          selectedTags,
          order,
          orderBy
        )
      );
      setIsLoading(false);
    }
    GetGlobalLists();
  }, [
    page,
    rowsPerPage,
    searchDebounce,
    selectedTags,
    order,
    orderBy,
    refreshTs,
  ]);

  const triggerRefresh = () => {
    setRefreshTs(Date.now());
    setPage(0);
  };

  const openCommentImportModal = () => {
    closeOptionModal();
    GetCLSummaryList(() => {
      setOpenCommentImport(true);
    });
  };

  const handleRetryClick = () => {
    setOpenCommentImport(true);
  };

  const closeCommentImportModal = () => setOpenCommentImport(false);

  const handleImportSuccess = () => {
    triggerRefresh();
    GetCLSummaryList();
    closeCommentImportModal();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    sessionStorage.setItem("listsRowPerPage", newRowsPerPage);
    setPage(0);
  };

  const handleEdit = (item, index) => {
    setEditDialogState((prev) => ({
      ...prev,
      open: "edit_list",
      listId: item.id,
      name: item.name || "",
      description: item.description || "",
      tags: item.tags || [],
      dialogOptionList: item.options || [],
      saveToGlobalLists: true,
      radioValue: "updateExisting",
      isGlobalList: true,
    }));
  };

  const handleDelete = async (item, index) => {
    setIsLoading(true);
    try {
      const payload = {
        list_id: item,
      };

      const response = await dispatch(deleteGlobalList(payload));
      if (response?.payload) {
        await dispatch(
          getGlobalList(
            page,
            rowsPerPage,
            searchDebounce,
            selectedTags,
            order,
            orderBy
          )
        );
      }
    } catch (error) {
      console.error("Delete error:", error);
    } finally {
      setIsLoading(false);
      setDeleteConfirm(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    setPage(0);
  };

  const handleAddNew = () => {
    setEditDialogState((prev) => ({
      ...prev,
      open: "new_list",
      saveToGlobalLists: true,
      dialogOptionList: [],
      name: "",
      tags: [],
      description: "",
      isGlobalList: true,
    }));
  };

  const handleSaveDialog = async (event) => {
    event?.stopPropagation();
    const hasValidOptions = editDialogState?.dialogOptionList.some(
      (option) => option && option?.trim().length > 0
    );
    if (!hasValidOptions) {
      console.error("At least one valid (non-empty) option is required");
      return;
    }
    const payload = {
      name: editDialogState.name,
      description: editDialogState.description,
      tag_ids: editDialogState?.tags?.map(
        (tag) => tag?.pivot?.tag_id || tag.id
      ),
      options: editDialogState?.dialogOptionList?.filter(
        (option) => option && option.trim() !== ""
      ),
    };
    try {
      setIsLoading(true);
      if (editDialogState.open === "new_list") {
        await dispatch(addGlobalList(payload));
      } else if (editDialogState.open === "edit_list") {
        if (editDialogState.radioValue === "updateExisting") {
          await dispatch(
            editGlobalList({ ...payload, list_id: editDialogState.listId })
          );
        } else if (editDialogState.radioValue === "saveAsNew") {
          await dispatch(addGlobalList(payload));
        }
      }
    } catch (error) {
      console.error("Error saving global list:", error.message);
    } finally {
      setIsLoading(false);
      setEditDialogState((prev) => ({ ...prev, open: false }));
    }
  };

  const hasActiveFilters = useMemo(() => {
    return (
      (searchDebounce && searchDebounce.trim() !== "") ||
      (Array.isArray(selectedTags) && selectedTags?.length > 0)
    );
  }, [searchDebounce, selectedTags]);

  const handleTagList = async () => {
    if (!sessionStorage.getItem("company_id")) return;
    const companyID = sessionStorage.getItem("company_id");
    try {
      const response = await getApiCall(
        `${API_CONSTANTS.FILTER_TAGS_LIST}?company_id=${companyID}`
      );
      if (response?.data?.success) {
        dispatch(globalListTag(response?.data?.data));
        return response.data.data;
      } else {
        console.error("API returned unsuccessful response");
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    handleTagList();
  }, []);

  useEffect(() => {
    if (globalList?.list_data?.length > 0) {
      setTags(globalList?.list_data);
    }
  }, [globalList?.list_data]);

  return (
    <>
      <Preloader showPreloader={isLoading} />
      <Box sx={{ width: "100%", padding: "20px 30px 20px 30px" }}>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          <Grid item xs={8}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ flex: "0 0 240px" }}>
                <TextField
                  fullWidth
                  placeholder="Search in table"
                  size="small"
                  value={search}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <SearchIcon
                        style={{
                          marginRight: 12,
                          marginTop: "2px",
                          color: "#7A7A7A",
                          fontSize: 18,
                        }}
                      />
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "40px",
                      padding: "0px 0px 0px 10px",
                      background: "#FFF",
                      borderRadius: "4px",
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#000000 !important",
                        borderWidth: "1px",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#D5D5D5 !important",
                        borderWidth: "1px",
                      },
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D5D5D5 !important",
                      borderWidth: "1px",
                    },
                  }}
                />
              </Box>
              <Box sx={{ width: "353px" }}>
                <FilterTags
                  filterTagList={tags}
                  selectedFilterTagID={selectedTags}
                  setSelectedFilterTagID={(newTags) => {
                    setSelectedTags(newTags);
                    setPage(0);
                  }}
                />
              </Box>
            </Box>
          </Grid>
          <Grid
            item
            xs={4}
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Box sx={{ display: "flex", gap: "8px" }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddNew}
                className="add-new-btn"
              >
                Add new
              </Button>
              <Button
                variant="outlined"
                startIcon={<Optionicon />}
                sx={{
                  textTransform: "none",
                  width: "100px",
                  height: "36px",
                  border: "1px solid #D5D5D5",
                  color: "#252525",
                }}
                onClick={openOptionModal}
              >
                Options
              </Button>
            </Box>
          </Grid>
        </Grid>
        <Paper
          sx={{
            width: "100%",
            mt: 1.2,
            border: "1px solid #E0E0E0",
            borderRadius: "4px",
            position: "relative",
            minHeight: "420px",
          }}
          elevation={0}
        >
          <TableContainer className="cl-table-container">
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: "20%", paddingLeft: "16px" }} >
                    <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>Name</Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, width: "50%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>Options</Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      width: "8%",
                      textAlign: "right",
                      paddingRight: "24px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: "4px",
                        alignItems: "center",
                        paddingRight: "32px",
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === "updated_at"}
                        direction={orderBy === "updated_at" ? order : "asc"}
                        onClick={() => handleRequestSort("updated_at")}
                        IconComponent={() => (
                          <ExpandlistMore
                            style={{
                              width: "16px",
                              height: "16px",
                              color:
                                orderBy === "updated_at" ? "#000" : "#757575",
                              transform:
                                orderBy === "updated_at" && order === "desc"
                                  ? "rotate(180deg)"
                                  : "none",
                              transition: "transform 0.2s",
                            }}
                          />
                        )}
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          width: "100%",
                        }}
                      >
                        Last modified
                      </TableSortLabel>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: "10%" }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {globalList?.globalListData?.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      sx={{ borderBottom: "none", height: "420px", p: 0 }}
                    >
                      <Box
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          sx={{ color: "text.secondary", fontSize: "14px" }}
                        >
                          {hasActiveFilters
                            ? "No results found"
                            : "List library is empty"}
                        </Typography>
                        {!hasActiveFilters && (
                          <Typography
                            sx={{
                              color: "#4282E2",
                              cursor: "pointer",
                              fontSize: "14px",
                              textDecoration: "underline",
                            }}
                            onClick={openCommentImportModal}
                          >
                            Import lists from templates
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  globalList?.globalListData?.map((item, index) => (
                    <TableRow key={`${item?.id || index}-${refreshTs}`} hover>
                      <TableCell
                        sx={{ width: "20%", paddingLeft: "16px", verticalAlign: "top", }}
                      >
                        <Typography sx={{ fontSize: 14 }}>
                          {(/^dropdown\s+[a-z0-9-]+$/i.test(item?.name)) ? "" : item?.name}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{ width: "50%", padding: "12px 16px", verticalAlign: "top", }}
                      >
                        <Typography
                          sx={{
                            color: "#252525",
                            fontSize: "14px",
                            lineHeight: "20px",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                          }}
                        >
                          {item?.options && item?.options?.length > 0
                            ? item?.options?.join(" | ")
                            : "-"}
                        </Typography>
                        <CommentTags tags={item?.tags} />
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "10%",
                          verticalAlign: "middle",
                          whiteSpace: "nowrap",
                          paddingLeft: "27px",
                        }}
                      >
                        <Box>
                          <Typography sx={{ fontSize: "14px" }}>
                            {item?.updated_at
                              ? moment(item?.updated_at).format("MM/DD/YYYY")
                              : "N/A"}
                          </Typography>
                          {item?.updated_at && (
                            <Typography
                              sx={{ fontSize: "12px", color: "#757575", mt: 0.3, }}
                            >
                              {moment(
                                moment.utc(item?.updated_at).local()
                              ).format("LT")}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            border: "1px solid #E0E0E0",
                            borderRadius: "6px",
                            background: "#fff",
                            height: 36,
                            width: "fit-content",
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(item, index)}
                            sx={{
                              padding: "6px",
                              minWidth: 36,
                              height: 36,
                              borderRadius: 0,
                              "&:hover": { background: "#f5f5f5" },
                            }}
                            aria-label="edit"
                          >
                            <EditIconSvg />
                          </IconButton>
                          <Divider
                            orientation="vertical"
                            flexItem
                            sx={{ borderColor: "#E0E0E0" }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => {
                              setDeleteConfirm(true);
                              setDeleteRowID(item);
                            }}
                            sx={{
                              padding: "6px",
                              minWidth: 36,
                              height: 36,
                              borderRadius: 0,
                              "&:hover": { background: "#f5f5f5" },
                            }}
                            aria-label="delete"
                          >
                            <DeleteIconSvg />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 200]}
          component="div"
          count={globalList?.globalListPagination?.total_count || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={TablePaginationActions}
        />
        <CLSettings
          open={openOption}
          onClose={closeOptionModal}
          openCommentImportModal={openCommentImportModal}
          importType="lists"
        />
        <CopySummaryModal
          open={deleteConfirm}
          handleClose={() => setDeleteConfirm(false)}
          handleSubmit={() => handleDelete(deleteRowID?.id)}
          submitButtonTitle={"Delete"}
          dialogTitle={`Delete ${!(/^dropdown\s+[a-z0-9-]+$/i.test(deleteRowID?.name)) ? deleteRowID?.name || "" : ""}`}
          dialogContentText={
            " Deleting a list will not remove it from the comments it is assigned to, but it will no longer show up as an option in your templates or future reports."
          }
        />
        <ImportComments
          key={"global-lists"}
          open={openCommentImport}
          onClose={closeCommentImportModal}
          data={importList}
          importing={importing}
          setImporting={setImporting}
          setProgress={setProgress}
          importType="lists"
          loading={isLoading}
          closeOptionModal={closeOptionModal}
          onImportSuccess={handleImportSuccess}
          onRetryClick={handleRetryClick}
        />
      </Box >
      <EditOptionsDialog
        state={editDialogState}
        setState={setEditDialogState}
        handleSave={handleSaveDialog}
        handleDeleteDropdown={() => { }}
        filterTagList={tags}
        id="globalList"
        canSave={
          editDialogState.dialogOptionList.some(
            (option) => option && option.trim().length > 0
          ) &&
          (!editDialogState.saveToGlobalLists ||
            (editDialogState.saveToGlobalLists &&
              editDialogState.name &&
              editDialogState.name.trim().length > 0))
        }
      />
    </>
  );
};

export default GlobalList;